import { useCallback, useState } from 'react';

import { cfetch } from '@confluence/network';

type UseApplyManagedLabelResult = {
	applyLabel: (
		labelId: string,
		entityAri: string,
		orgId: string,
		workspaceAri: string,
	) => Promise<boolean>;
	loading: boolean;
	error: Error | null;
};

/**
 * Provides a function to apply (assign) a managed label to an entity via the
 * cust-data-classifier GraphQL gateway.
 *
 * Uses the `tags_assignLabel` mutation to assign an existing managed label to
 * content. The label `kind` is `managed-label` (the kind these labels are
 * stored under; confirmed against real network responses on the staging tenant).
 */
export const useApplyManagedLabel = (): UseApplyManagedLabelResult => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const applyLabel = useCallback(
		async (
			labelId: string,
			entityAri: string,
			orgId: string,
			workspaceAri: string,
		): Promise<boolean> => {
			setLoading(true);
			setError(null);

			try {
				const response = await cfetch('/gateway/api/graphql', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						query: `mutation managedAssignLabel($kind: String!, $input: TagsAssignLabelInput!) {
							tags_assignLabel(kind: $kind, input: $input) {
								entityAri
								labelId
							}
						}`,
						variables: {
							kind: 'managed-label',
							input: {
								entityAri,
								labelId,
								orgId,
								workspaceAri,
							},
						},
					}),
				});

				if (!response.ok) {
					throw new Error(`Failed to apply managed label: ${response.status}`);
				}

				const result = await response.json();

				if (result.errors?.length) {
					throw new Error(result.errors[0].message);
				}

				return true;
			} catch (err) {
				const applyErr = err instanceof Error ? err : new Error(String(err));
				setError(applyErr);
				return false;
			} finally {
				setLoading(false);
			}
		},
		[],
	);

	return {
		applyLabel,
		loading,
		error,
	};
};
